import axios from 'axios';

const DEFAULT_ORIGIN =
  typeof window !== 'undefined' ? window.location.origin : 'http://localhost:3000';

type BackendConfig = {
  origin: string;
  basePath: string;
};

let cachedBaseURL: string | null = null;
let cachedBackendConfig: BackendConfig | null = null;

const resolveBackendConfig = (): BackendConfig => {
  const configured = axios.defaults.baseURL || DEFAULT_ORIGIN;
  if (cachedBackendConfig && configured === cachedBaseURL) {
    return cachedBackendConfig;
  }

  let origin = DEFAULT_ORIGIN;
  let basePath = '';
  try {
    const parsed = new URL(configured, DEFAULT_ORIGIN);
    origin = parsed.origin;
    basePath = parsed.pathname.replace(/\/$/, '');
    if (basePath === '/') {
      basePath = '';
    }
  } catch {
    origin = DEFAULT_ORIGIN;
    basePath = '';
  }

  cachedBaseURL = configured;
  cachedBackendConfig = { origin, basePath };
  return cachedBackendConfig;
};

export const getBackendOrigin = (): string => resolveBackendConfig().origin;
export const getBackendBasePath = (): string => resolveBackendConfig().basePath;

export const getImageUrl = (rawPath: string | null | undefined): string => {
  if (!rawPath) return '';
  const path = rawPath.trim();
  if (!path) return '';
  if (path.startsWith('blob:') || path.startsWith('data:')) {
    return path;
  }

  const { origin: backendOrigin, basePath: backendBasePath } = resolveBackendConfig();
  const backendUrl = new URL(backendOrigin);
  const basePathPrefix = backendBasePath && backendBasePath !== '/' ? backendBasePath : '';

  const applyBasePath = (targetPath: string) => {
    if (targetPath.startsWith('/api/')) {
      return targetPath;
    }
    if (targetPath.startsWith('/uploads/') && basePathPrefix) {
      return `${basePathPrefix}${targetPath}`;
    }
    if (targetPath.startsWith('/uploads/')) {
      return targetPath;
    }
    return `${basePathPrefix}${targetPath}`;
  };

  const buildFromRelative = (relativePath: string) => {
    if (!relativePath) return '';
    const normalized = relativePath.replace(/\\/g, '/');
    const withLeadingSlash = normalized.startsWith('/') ? normalized : `/${normalized}`;
    const finalPath = applyBasePath(withLeadingSlash);
    return `${backendOrigin}${finalPath}`;
  };

  const normalizedPath = path.replace(/\\/g, '/');
  const uploadsIndex = normalizedPath.indexOf('/uploads/');

  if (/^https?:\/\//i.test(normalizedPath)) {
    try {
      const absoluteUrl = new URL(normalizedPath);

      if (absoluteUrl.pathname.includes('/uploads/')) {
        return buildFromRelative(absoluteUrl.pathname);
      }

      if (
        typeof window !== 'undefined' &&
        window.location.protocol === 'https:' &&
        absoluteUrl.protocol === 'http:' &&
        absoluteUrl.hostname === backendUrl.hostname
      ) {
        absoluteUrl.protocol = 'https:';
        return absoluteUrl.href;
      }

      return absoluteUrl.href;
    } catch {
      return normalizedPath;
    }
  }

  if (uploadsIndex >= 0) {
    return buildFromRelative(normalizedPath.slice(uploadsIndex));
  }

  return buildFromRelative(normalizedPath);
};
